import React, { useState, useEffect } from "react";
import BaseTailoringQuestions from "../shared/BaseTailoringQuestions";
import type { TailoringQuestion } from "../shared/BaseTailoringQuestions";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { AlertCircle, FileText, FileSignature } from "lucide-react";
import estimatesData from "../../constants/ConcludingProcedures/Estimates.json";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface ReviewOfEstimatesForManagementBiasLandingProps {
  onSelect?: (section: string) => void;
}

// Transform JSON structure to TailoringQuestion format
function transformJSONToQuestions(jsonData: any): TailoringQuestion[] {
  const questions: TailoringQuestion[] = [];
  const data = jsonData.Review_of_Estimates_for_Management_Bias;

  // Helper to create a question
  const createQuestion = (
    id: string,
    questionText: string,
    type: "radio" | "text" | "table",
    options?: string[],
    followUp?: TailoringQuestion["follow_up"],
    tableConfig?: TailoringQuestion["tableConfig"],
    guidance?: TailoringQuestion["guidance"]
  ): TailoringQuestion => {
    return {
      id,
      question: questionText,
      type,
      options,
      follow_up: followUp,
      tableConfig,
      guidance,
    };
  };

  // REMB-1: Overview - Estimates Identification Table
  if (data.Overview?.Estimates_Identification) {
    const section = data.Overview.Estimates_Identification;
    const table = section.table;
    
    if (table && table.columns && table.inputs) {
      const columns = table.columns.map((col: string, index: number) => {
        const inputType = table.inputs[index];
        let type = "text";
        let options: string[] | undefined = undefined;

        if (inputType === "Dropdown – Yes/No") {
          type = "yes_no";
        } else if (inputType === "Dropdown of either Low or High") {
          type = "dropdown";
          options = ["Low", "High"];
        } else if (inputType.includes("Dropdown")) {
          type = "text"; // For "Dropdown of all execution areas added by user", use text for now
        }

        return {
          key: `col_${index}`,
          label: col,
          type,
          options,
        };
      });

      questions.push(
        createQuestion(
          "REMB-1",
          section.instruction || "Document the estimates identified by us in the table below:",
          "table",
          undefined,
          undefined,
          {
            columns,
            allowAdd: true,
            allowDelete: true,
          }
        )
      );
    }
  }

  // REMB-2: Procedures - Always conditions (documentation)
  if (data.Procedures && data.Procedures.length > 0) {
    data.Procedures.forEach((proc: any, index: number) => {
      if (proc.condition === "Always") {
        // Check if this procedure has guidance
        const guidance = proc.guidance ? {
          text: "", // No introductory text, just items
          items: proc.guidance.items || []
        } : undefined;

        questions.push(
          createQuestion(
            `REMB-2-${index + 1}`,
            proc.output,
            "text",
            undefined,
            undefined,
            undefined,
            guidance
          )
        );
      }
    });
  }

  // REMB-3: Assessment of Management Bias Table
  if (data.Assessment_of_Management_Bias) {
    const section = data.Assessment_of_Management_Bias;
    const table = section.table;
    
    if (table && table.columns && table.inputs) {
      const columns = table.columns.map((col: string, index: number) => {
        const inputType = table.inputs[index];
        let type = "text";
        let options: string[] | undefined = undefined;
        let readOnly = false;
        let sourceColumn: string | undefined = undefined;

        if (inputType === "Dropdown – Yes/No") {
          type = "yes_no";
        } else if (inputType === "Coming from above") {
          type = "readonly"; // Mark as read-only
          readOnly = true;
          // Map column names from first table to second table
          if (col === "Name of estimate") {
            sourceColumn = "col_0"; // First column in REMB-1 (Name of Estimate)
          } else if (col === "ABCOTD") {
            sourceColumn = "col_1"; // Second column in REMB-1 (ABCOTD)
          }
        }

        return {
          key: `col_${index}`,
          label: col,
          type,
          options,
          readOnly,
          sourceColumn,
        };
      });

      questions.push(
        createQuestion(
          "REMB-3",
          section.instruction || "Document in the table below:",
          "table",
          undefined,
          undefined,
          {
            columns,
            allowAdd: true,
            allowDelete: true,
            sourceQuestionId: "REMB-1", // Indicate that this table gets data from REMB-1
          }
        )
      );
    }
  }

  // REMB-4: Documentation - Always condition
  if (data.Documentation && data.Documentation.length > 0) {
    data.Documentation.forEach((doc: any, index: number) => {
      if (doc.condition === "Always") {
        questions.push(
          createQuestion(
            `REMB-4-${index + 1}`,
            doc.output,
            "text"
          )
        );
      }
    });
  }

  return questions;
}

const WORKSPACE_NAME = "Review of estimates for management bias";

export default function ReviewOfEstimatesForManagementBiasLanding(_props: ReviewOfEstimatesForManagementBiasLandingProps) {
  const [questions, setQuestions] = useState<TailoringQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the imported JSON data directly
        const jsonData = estimatesData;

        const transformedQuestions = transformJSONToQuestions(jsonData);
        setQuestions(transformedQuestions);
      } catch (err) {
        console.error("Failed to load questions:", err);
        setError(err instanceof Error ? err.message : "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, []);

  if (loading) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Review of estimates for management bias</h2>
          <p className="text-white/60">Loading questions...</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading questions from JSON file...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Review of estimates for management bias</h2>
          <p className="text-white/60">Error loading questions</p>
        </div>
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Review of estimates for management bias</h2>
          <p className="text-white/60">No questions found</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <FileText className="h-8 w-8 mx-auto mb-4 text-white/40" />
              <p>No questions available. Please check the JSON file.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2 flex-wrap px-6 pt-2">
        <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
        <Button variant="outline" size="sm" onClick={() => setSignoffDialogOpen(true)} className="border-white/20 text-white hover:bg-white/10">
          <FileSignature className="h-4 w-4 mr-1.5" />
          Sign off
        </Button>
      </div>
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <BaseTailoringQuestions
        areaName="REMB"
        title="Review of estimates for management bias"
        description="Answer the following questions based on review of estimates for management bias requirements."
        questions={questions}
        onComplete={(answers) => {
          console.log("Review of estimates for management bias answers:", answers);
        }}
      />
    </div>
  );
}
